// Runtime "am I on the latest build?" check.
//
// On every load we ask the backend for its APP_VERSION and compare it against
// the version this browser saw last time. If it changed we:
//   1. wipe the Cache Storage API (that's where SW caches + stale HTTP
//      responses actually live for a hosted SPA);
//   2. tell any waiting Service Worker to activate immediately;
//   3. bump the stored version and hard-reload the tab ONCE (a sessionStorage
//      guard prevents reload loops if the backend keeps returning a new value).
//
// We deliberately do NOT touch localStorage / sessionStorage / IndexedDB /
// cookies, which is where the Google-login token + AuthContext session live.
// That means the reload feels instant and the user stays signed in.

const VERSION_KEY = "em_app_version";
const RELOADED_KEY = "em_version_reloaded";
const JUST_UPDATED_KEY = "em_version_just_updated";

// After an auto-reload onto a new build, say so once the app is back up.
// (Stashed right before the reload; toasted here on the fresh boot.)
function announceJustUpdated() {
  try {
    const v = sessionStorage.getItem(JUST_UPDATED_KEY);
    if (!v) return;
    sessionStorage.removeItem(JUST_UPDATED_KEY);
    setTimeout(async () => {
      try {
        const { toast } = await import("sonner");
        toast.success(`Updated to the latest version (${v})`, { duration: 6000 });
      } catch (e) { /* toaster not mounted yet */ }
    }, 1200);
  } catch (e) { /* private mode */ }
}

export async function runVersionCheck() {
  if (typeof window === "undefined") return;
  announceJustUpdated();
  const backend = process.env.REACT_APP_BACKEND_URL;
  if (!backend) return;
  try {
    const res = await fetch(`${backend}/api/version`, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!res.ok) return;
    const { version } = await res.json();
    if (!version) return;

    const stored = localStorage.getItem(VERSION_KEY);

    if (!stored) {
      // First visit ever — just remember the version, don't reload.
      localStorage.setItem(VERSION_KEY, version);
      sessionStorage.removeItem(RELOADED_KEY);
      return;
    }

    if (stored === version) {
      // Clean slate — clear the reload guard so future updates can trigger.
      sessionStorage.removeItem(RELOADED_KEY);
      return;
    }

    // Guard: only reload once per session, even if the backend momentarily
    // reports two different versions (e.g. rolling deploy).
    if (sessionStorage.getItem(RELOADED_KEY)) {
      localStorage.setItem(VERSION_KEY, version);
      return;
    }

    // 1) Purge every Cache Storage entry (SW precache + HTTP mirror).
    try {
      if (window.caches && caches.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) { /* ignore */ }

    // 2) Nudge any waiting Service Worker to take over immediately so the
    //    next reload gets fresh assets.
    try {
      if (navigator.serviceWorker) {
        const reg = await navigator.serviceWorker.getRegistration("/sw.js");
        if (reg?.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
        try { await reg?.update(); } catch (e) { /* offline */ }
      }
    } catch (e) { /* ignore */ }

    localStorage.setItem(VERSION_KEY, version);
    sessionStorage.setItem(RELOADED_KEY, "1");
    try { sessionStorage.setItem(JUST_UPDATED_KEY, version); } catch (e) { /* ignore */ }
    window.location.reload();
  } catch (e) {
    // Network / CORS blip — never break app boot for a version check.
  }
}
